import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { trackEvent } from "../../utils/analytics";
import { useReminderSetupPrompt } from "./hooks/useReminderSetupPrompt";
import { useReminderSetupStatus } from "./hooks/useReminderSetupStatus";
import ReminderSetupPromptSheet from "./ReminderSetupPromptSheet";
import { PROMPT_STORAGE_ENABLED } from "./reminderPromptStorage";
import { useNavigateToReminders } from "./useNavigateToReminders";

const AUTH_PATHS = new Set([
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
]);

const ReminderSetupPromptHost = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const navigateToReminders = useNavigateToReminders();

  const onRemindersPage = location.pathname === "/reminders";
  const onAuthPage = AUTH_PATHS.has(location.pathname);
  const canCheckReminderStatus = !onRemindersPage && !onAuthPage;

  const { isReminderSetup, isLoading } = useReminderSetupStatus(
    canCheckReminderStatus,
  );

  const reminderStatusKnown = !isAuthenticated || !isLoading;

  const enabled = useMemo(() => {
    if (!canCheckReminderStatus) return false;
    if (isAuthenticated) {
      if (isLoading) return false;
      if (isReminderSetup) return false;
    }
    if (!PROMPT_STORAGE_ENABLED) return true;
    return reminderStatusKnown && !isReminderSetup;
  }, [
    canCheckReminderStatus,
    isAuthenticated,
    isLoading,
    isReminderSetup,
    reminderStatusKnown,
  ]);

  const {
    open: reminderPromptOpen,
    dismiss: dismissReminderPrompt,
    close: closeReminderPrompt,
  } = useReminderSetupPrompt({ enabled });

  const handleReminderPromptSetUp = () => {
    closeReminderPrompt();
    trackEvent("reminder_prompt_setup");
    navigateToReminders("prompt_sheet");
  };

  return (
    <ReminderSetupPromptSheet
      open={reminderPromptOpen}
      onClose={closeReminderPrompt}
      onDismiss={dismissReminderPrompt}
      onSetUp={handleReminderPromptSetUp}
    />
  );
};

export default ReminderSetupPromptHost;
